/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router-dom components

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/system";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// sweetalert2 components

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import CreatSignal from "Modals/CreateSignal";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

// ProductsList page components

import { getData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

let availableProviders = null;

/**
 *
 * @returns
 */
function SignalsList() {
  const [controller, dispatch] = useSoftUIController();
  const { platform, permissions, sports, providers } = controller;

  const [showCreateModal, setCreateModal] = useState(false);
  // const [availableProviders, setAvailableProviders] = useState(null);
  const [allowCreate, setAllowCreate] = useState(true);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [loading, setLoading] = useState(true);
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "id", accessor: "id", width: "100px" },
      { Header: "type", accessor: "type", width: "120px" },
      { Header: "platformId", accessor: "platform_id", width: "120px", Cell: renderPlatform },
      { Header: "processed", accessor: "processed", width: "120px", Cell: renderProcessCell },
      {
        Header: "created at",
        accessor: "created_at",
        width: "120px",
        Cell: formatDateStringUTCtoLocale,
      },
      { Header: "status", accessor: "status", width: "120px", Cell: renderStatusCell },
      { Header: "recipient", accessor: "recipient", width: "120px" },
      { Header: "data", accessor: "data", width: "120px", Cell: renderData },
      { Header: "user", accessor: "username", width: "120px" },
    ],

    rows: [],
  });

  const userdata = getUserData();
  const platforms = userdata.platforms;

  /**
   *
   * @param {*} param0
   * @returns
   */
  function renderPlatform({ value, ...rest }) {
    let platform = platforms ? platforms.find((p) => p.id == value) : null;
    return platform ? platform["name"] : value == 0 ? "-" : value;
  }
  /**
   *
   * @param {*} param0
   * @returns
   */
  function renderData({ value, ...rest }) {
    let valueProviders = value ? value.replace(/[\[\]\\{}"]/g, "") : null;
    if (!valueProviders) return "-";
    valueProviders = valueProviders.split(",");
    return (
      <div className="signal-data-processed">
        {valueProviders
          .map((provider) =>
            availableProviders && availableProviders.find((p) => p.value == provider.trim())
              ? availableProviders.find((p) => p.value == provider.trim())["label"]
              : provider.trim()
          )
          .join(", ")}
      </div>
    );
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function renderStatusCell({ value, ...rest }) {
    return value === true ? (
      <SoftBadge variant="contained" color="success" size="xs" badgeContent="Completed" container />
    ) : value === false ? (
      <SoftBadge variant="contained" color="error" size="xs" badgeContent="Failed" container />
    ) : (
      <SoftBadge variant="contained" color="warning" size="xs" badgeContent="Pending" container />
    );
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function renderProcessCell({ value, ...rest }) {
    return value === true ? (
      <Icon sx={{ color: "rgb(130, 214, 22)" }}>check</Icon>
    ) : (
      <Icon sx={{ color: "red" }}>close</Icon>
    );
  }

  function formatDateStringUTCtoLocale({ value, ...rest }) {
    if (!value) return "-";
    let date = new Date(value);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

    let dateformatted = day + "/" + month + "/" + date.getFullYear() + " " + hours + ":" + minutes;
    let timezone = (date.getTimezoneOffset() * -1) / 60;
    timezone = timezone > 0 ? "+" + timezone : timezone;

    return <div>{dateformatted}</div>;
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseModal = async (update) => {
    if (update) {
      initData();
    }
    setCreateModal(false);
  };

  /**
   *
   */
  const handleCreateRecord = () => {
    setCreateModal(true);
  };

  /**
   *
   */
  const handleInformParent = () => {
    initData();
  };

  /**
   *
   */
  async function initData() {
    setLoading(true);
    let getSignalsEndpoint = `/api/v1/signals`;

    let response = await getData(getSignalsEndpoint, { Authorization: getAuthorizationHeader() });

    let data = { ...dataTableData };
    data.rows = response && response.data ? response.data : data.rows;

    data.rows.sort((a, b) => b.id - a.id);

    //console.log("Init Data");
    setDataTableData(data);
    setLoading(false);
  }

  useEffect(() => {
    if (providers) {
      availableProviders = providers;
      initData();
    }
  }, [providers]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <CreatSignal showModal={showCreateModal} signalCloseModal={handleCloseModal}></CreatSignal>

      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Your Signals
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all Signals send to Clients through the Panel Processes.
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleCreateRecord}
                style={{ display: allowCreate ? "" : "none" }}
              >
                + new Signal
              </SoftButton>
            </Stack>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            canSearch
            refreshEnabled={true}
            openBackDrop={loading}
            informParent={handleInformParent}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default SignalsList;
